import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, Reveal, SectionHeading, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, FALLBACK_BRANCHES, CLASSES } from "@/data/site";

const MARQUEE_ITEMS = [
  "KG Halli",
  "Lingarajapuram",
  "L R Bande",
  "Strong Body. Strong Mind. Strong Life.",
  "Strength · Cardio · Combat · Yoga",
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] flex items-end overflow-hidden noise">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.hero}
          alt="Athlete training at Oxygen Gym"
          className="h-full w-full object-cover object-center scale-105 opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 60% 50% at 75% 30%, rgba(227,184,76,0.16), transparent 70%)" }}
        />
      </motion.div>

      <motion.div style={{ opacity: contentFade }} className="mx-auto w-full max-w-7xl px-4 sm:px-8 lg:px-12 pb-20 md:pb-28 pt-40">
        <MaskedLine delay={0.1}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6">
            Oxygen Gym — Fitness & Gym · Bengaluru
          </p>
        </MaskedLine>
        <h1 className="font-display uppercase leading-[0.92] text-[clamp(3.2rem,10vw,8.5rem)] tracking-tight">
          <MaskedLine delay={0.2}>Strong Body.</MaskedLine>
          <MaskedLine delay={0.34}>
            <span className="text-gold-gradient">Strong Mind.</span>
          </MaskedLine>
          <MaskedLine delay={0.48}>Strong Life.</MaskedLine>
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-8">
          <MaskedLine delay={0.62}>
            <p className="max-w-md text-white/70 text-sm md:text-base leading-relaxed">
              Three floors of iron across KG Halli, Lingarajapuram & L R Bande. Real equipment, real
              coaching, no shortcuts.
            </p>
          </MaskedLine>
          <MaskedLine delay={0.72}>
            <div className="flex flex-wrap gap-4">
              <Link
                data-testid="hero-cta-start"
                to="/contact"
                className="bg-gold text-ink px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
              >
                Join Oxygen Gym
              </Link>
              <Link
                data-testid="hero-cta-branches"
                to="/branches"
                className="border border-white/25 text-white px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Explore Branches
              </Link>
            </div>
          </MaskedLine>
        </div>
        <MaskedLine delay={0.85}>
          <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
            <span><span className="text-gold">03</span> Branches</span>
            <span><span className="text-gold">06</span> AM Doors Open</span>
            <span><span className="text-gold">01</span> Standard</span>
          </div>
        </MaskedLine>
      </motion.div>
    </section>
  );
}

function BranchCards() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-20 md:py-28">
      <SectionHeading
        eyebrow="Our Houses"
        title={<>Three floors of iron.<br />One standard.</>}
        link
        linkLabel="All branches"
        linkTo="/branches"
      />
      <div className="grid gap-6 md:grid-cols-3">
        {FALLBACK_BRANCHES.map((b, i) => (
          <Reveal key={b.slug} delay={i * 0.12}>
            <Link
              data-testid={`home-branch-card-${b.slug}`}
              to={`/branches/${b.slug}`}
              className="group block relative overflow-hidden clip-frame border border-white/10 h-full"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={b.image}
                  alt={`Oxygen Gym ${b.name} interior`}
                  className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Branch 0{i + 1}</p>
                <h3 className="font-display uppercase text-2xl">{b.name}</h3>
                <p className="mt-1 text-xs text-white/60">{b.address}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  View branch →
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Bento() {
  const preview = CLASSES.slice(0, 4);
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
      <SectionHeading eyebrow="The Oxygen Standard" title="Built different. Built heavy." />
      <div className="grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-7" delay={0}>
          <div className="relative h-full min-h-[380px] overflow-hidden clip-frame border border-white/10 group">
            <img
              src={IMAGES.barbell}
              alt="Heavy barbell training at Oxygen Gym"
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Signature Floor</p>
              <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight max-w-md">
                Strength & Hypertrophy
              </h3>
              <p className="mt-3 text-sm text-white/60 max-w-md">
                Hammer Strength racks, calibrated plates and coaches who count every rep with you.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <div className="h-full clip-frame border border-white/10 bg-panel p-8 md:p-10 flex flex-col">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-6">Programs</p>
            <ul className="flex-1 space-y-4">
              {preview.map((c) => (
                <li key={c.id} className="flex items-center justify-between border-b border-white/5 pb-4">
                  <span className="font-display uppercase text-lg">{c.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{c.category}</span>
                </li>
              ))}
            </ul>
            <Link
              data-testid="bento-view-classes"
              to="/classes"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors"
            >
              All classes <span aria-hidden="true">→</span>
            </Link>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-4" delay={0}>
          <div className="relative h-full min-h-[240px] overflow-hidden clip-frame border border-white/10 group">
            <img
              src={IMAGES.pullup}
              alt="Athlete doing pull-ups"
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
            <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              No shortcuts. Only reps.
            </p>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-4" delay={0.1}>
          <div className="h-full min-h-[240px] clip-frame border border-white/10 bg-panel p-8 flex flex-col justify-center">
            <p className="font-display uppercase text-5xl text-gold-gradient">₹1,500</p>
            <p className="font-display uppercase text-xl text-white/80 mt-2">per month onwards</p>
            <p className="mt-4 text-sm text-white/60">
              Real prices at every branch — yearly from ₹5,000. Pay at the reception desk.
            </p>
            <Link
              data-testid="bento-view-pricing"
              to="/pricing"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors"
            >
              See plans <span aria-hidden="true">→</span>
            </Link>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-4" delay={0.2}>
          <div className="h-full min-h-[240px] clip-frame border border-white/10 bg-panel p-8 flex flex-col justify-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Doors Open</p>
            <p className="font-display uppercase text-6xl leading-none">06:00</p>
            <p className="mt-4 text-sm text-white/60">Mon–Sat from 6 AM — KG Halli runs till 11:45 PM. Closed Sundays.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Gallery() {
  const shots = [
    { src: IMAGES.interiorLed, label: "The LED Floor — KG Halli" },
    { src: IMAGES.kettlebells, label: "Kettlebell Zone" },
    { src: IMAGES.dumbbells, label: "Free Weights Wall" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-20 md:pb-28">
      <SectionHeading eyebrow="Inside Oxygen" title="Where the work happens." />
      <div className="grid gap-6 md:grid-cols-3">
        {shots.map((s, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <div className="group relative overflow-hidden clip-frame border border-white/10">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={s.src}
                  alt={s.label}
                  className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24 md:pb-32">
      <Reveal>
        <div className="relative overflow-hidden clip-frame border border-gold/30 noise bg-panel px-8 py-16 md:py-24 text-center"
          style={{ background: "radial-gradient(ellipse 70% 100% at 50% 100%, rgba(227,184,76,0.14), #12141A 75%)" }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6">First visit? Walk in strong.</p>
          <h2 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Ready to <span className="text-gold-gradient">breathe?</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-lg mx-auto text-sm md:text-base">
            Drop an enquiry and our team at your nearest branch will call you back the same day.
          </p>
          <Link
            data-testid="home-cta-enquiry"
            to="/contact"
            className="mt-10 inline-block bg-gold text-ink px-10 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
          >
            Start Your Enquiry
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <BranchCards />
      <Bento />
      <Gallery />
      <CtaBanner />
    </main>
  );
}
